import type { AtcObjectRedraw, CartRule, CartRuleResult } from '@/features/Cart/types/models';
import type { ShopifyCart, ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';
import { cartRuleVariantParentMaxQuantity } from '@/features/Cart/cartRules/variantParentMaxQuantity';
import { cartRuleFreeGift } from '@/features/Cart/cartRules/freeGift';
import { cartRuleBundleMaxCartQuantity } from '@/features/Cart/cartRules/bundleMaxCartQuantity';
import { cartRuleBulkProductMaxQuantity } from '@/features/Cart/cartRules/bulkProductMaxQuantity';

const applyRuleResult = (
  items: ShopifyCartItem[],
  result: CartRuleResult | null,
): ShopifyCartItem[] => {
  if (!result) return items;
  const removedKeys = new Set(result.remove.map(({ key }) => key));
  const updatedQuantities = new Map(result.update.map(({ key, quantity }) => [key, quantity]));
  return items
    .filter((item) => !removedKeys.has(item.key))
    .map((item) =>
      updatedQuantities.has(item.key)
        ? { ...item, quantity: updatedQuantities.get(item.key) as number }
        : item,
    );
};

export const getCartRuleChanges = (cart: ShopifyCart): CartRuleResult => {
  const cartRules: CartRule[] = [
    cartRuleFreeGift,
    cartRuleVariantParentMaxQuantity,
    cartRuleBundleMaxCartQuantity,
    cartRuleBulkProductMaxQuantity,
  ];
  const changes: CartRuleResult = { add: [], update: [], remove: [] };
  const addRedraws: AtcObjectRedraw[] = [];
  let workingItems = cart.items;
  cartRules.forEach((rule) => {
    const result = rule({ ...cart, items: workingItems });
    if (!result) return;
    changes.add.push(...result.add);
    changes.update.push(...result.update);
    changes.remove.push(...result.remove);
    if (result.redrawAdd) addRedraws.push(result.redrawAdd);
    workingItems = applyRuleResult(workingItems, result);
  });
  if (addRedraws.length) {
    changes.redrawAdd = (items, variantIds) =>
      addRedraws.reduce(
        (redrawnItems, redraw) =>
          redrawnItems.length ? redraw(redrawnItems, variantIds) : redrawnItems,
        items,
      );
  }
  return changes;
};
